import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { buildProviders, Coerce, KalDate } from '@kalidea/kaligraphi';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { getKalDateFromObject } from 'src/app/utils/date.utils';
import { updateValueAndValidity } from 'src/app/utils/form.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';

export interface DateRange {
    startDate: KalDate;
    endDate: KalDate;
}

@Component({
    selector: 'app-date-range',
    templateUrl: './date-range.component.html',
    styleUrls: ['./date-range.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(DateRangeComponent),
    standalone: false
})
export class DateRangeComponent extends CustomFormControlAccess implements OnInit {

    @Input() controlLabel = 'Date';

    @Input() startPlaceholder = 'A partir du';

    @Input() endPlaceholder = 'Jusqu\'au';

    @Input() betweenLabel = 'au';

    @Input() id = 'date_range_filter';

    @Input()
    @Coerce('boolean')
    required = false;

    @Input()
    @Coerce('boolean')
    displayDateEnd = false;

    form: FormGroup;

    @Input()
    addDatesLabel = 'Définir une période';

    private minDateSubject = new BehaviorSubject<KalDate>(null);

    private maxDateSubject = new BehaviorSubject<KalDate>(null);

    constructor(private readonly fb: FormBuilder) {
        super();
    }

    @Input()
    set minDate(value: KalDate) {
        this.minDateSubject.next(value);
    }

    get minDate$(): Observable<KalDate> {
        return this.minDateSubject.asObservable();
    }

    @Input()
    set maxDate(value: KalDate) {
        this.maxDateSubject.next(value);
    }

    get maxDate$(): Observable<KalDate> {
        return this.maxDateSubject.asObservable();
    }

    writeValue(value: any): void {
        const format = environment.date.format.display;
        this.form.patchValue({
            startDate: getKalDateFromObject(value, 'startDate', format),
            endDate: getKalDateFromObject(value, 'endDate', format),
        }, {emitEvent: false});

        const formValues = this.form.value;

        if (formValues.endDate && !this.displayDateEnd) {
            this.toggleDateEnd();
        }

        this.setDatesValidators(formValues);
    }

    validate(c: AbstractControl): Observable<any> {
        return of(this.form.valid ? null : {dateRange: true});
    }

    toggleDateEnd(): void {
        this.displayDateEnd = !this.displayDateEnd;
    }

    private setDatesValidators(formValues: any): void {
        const dateStartControl = this.form.get('startDate');
        const dateEndControl = this.form.get('endDate');
        const dateStart = formValues.startDate as KalDate;
        const dateEnd = formValues.endDate as KalDate;
        const minDateSubjectValue = this.minDateSubject?.value;
        const maxDateSubjectValue = this.maxDateSubject?.value;

        let minStartDate: KalDate = null;
        let minDateEndControlValue: KalDate = null;

        let maxEndDate: KalDate = null;
        let maxDateStartControlValue: KalDate = null;

        if (dateStart && minDateSubjectValue?.isAfter(dateStart)) {
            minStartDate = dateStart;
            minDateEndControlValue = minDateSubjectValue;
        } else {
            minDateEndControlValue = dateStart || minDateSubjectValue;
            minStartDate = minDateSubjectValue || dateStart;
        }

        if (dateEnd && maxDateSubjectValue?.isAfter(dateEnd)) {
            maxEndDate = maxDateSubjectValue;
            maxDateStartControlValue = dateEnd;
        } else {
            maxDateStartControlValue = dateEnd || maxDateSubjectValue;
            maxEndDate = maxDateSubjectValue || dateEnd;
        }

        dateEndControl.clearValidators();
        dateStartControl.clearValidators();

        if (minStartDate && minStartDate?.valid) {
            const dateEndValidators = [
                FormValidator.date,
                FormValidator.minDateValidator(minDateEndControlValue)
            ];
            if (!!maxDateSubjectValue && maxEndDate) {
                dateEndValidators.push(FormValidator.maxDateValidator(maxEndDate));
            }
            dateEndControl.setValidators(dateEndValidators);
        } else {
            dateStartControl.setValidators(FormValidator.date);
        }

        if (maxEndDate && maxEndDate?.valid) {
            const dateStartValidators = [
                FormValidator.date,
                FormValidator.maxDateValidator(maxDateStartControlValue)
            ];
            if (!!minDateSubjectValue && minStartDate) {
                dateStartValidators.push(FormValidator.minDateValidator(minStartDate));
            }
            dateStartControl.setValidators(dateStartValidators);
        } else {
            dateEndControl.setValidators(FormValidator.date);
        }

        updateValueAndValidity(this.form, {emitEvent: false});
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            startDate: [null, [FormValidator.date]],
            endDate: [null, [FormValidator.date]]
        }, {
            validator: FormValidator.dateRangeValidator('startDate', 'endDate')
        });

        this.watch$ = this.minDate$.pipe(
            distinctUntilChanged(isEqual),
            tap(() => this.setDatesValidators(this.form.value))
        );

        this.watch$ = this.maxDate$.pipe(
            distinctUntilChanged(isEqual),
            tap(() => this.setDatesValidators(this.form.value))
        );

        this.watch$ = this.form.valueChanges.pipe(
            tap(formValues => {
                this.setDatesValidators(formValues);
                this.notifyUpdate(formValues);
            })
        );
    }

}
